import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { useToast } from "./use-toast";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Report fetching + login gating state
const useReportState = () => {
  const [report, setReport] = useState(null);
  const [needLogin, setNeedLogin] = useState(false);
  const [pendingReportId, setPendingReportId] = useState(null);

  const fetchReport = useCallback(async (id) => {
    try {
      const res = await axios.get(`${API}/seo/report/${id}`);
      setReport(res.data);
      setNeedLogin(false);
    } catch (e) {
      if (e?.response?.status === 401) {
        setPendingReportId(id);
        setNeedLogin(true);
      } else {
        console.error("Failed to fetch report:", e);
      }
    }
  }, []);

  return { report, setReport, needLogin, setNeedLogin, pendingReportId, setPendingReportId, fetchReport };
};

// Stripe checkout, payment-return polling and deep-link handling
const usePaymentFlow = (report, fetchReport, toast) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [paying, setPaying] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const paymentHandledRef = useRef(false);

  const pollStatus = useCallback(async (sessionId, reportId, attempt = 0) => {
    const MAX_ATTEMPTS = 8;
    if (attempt >= MAX_ATTEMPTS) {
      setVerifying(false);
      toast({ title: "Still processing", description: "Payment is taking longer than expected. Refresh in a moment.", variant: "destructive" });
      return;
    }
    try {
      const res = await axios.get(`${API}/seo/payment/status/${sessionId}`);
      if (res.data.payment_status === "paid") {
        setVerifying(false);
        toast({ title: "Payment successful!", description: "Your full SEO report is now unlocked." });
        await fetchReport(reportId);
        setSearchParams({});
        return;
      }
      if (res.data.status === "expired") {
        setVerifying(false);
        toast({ title: "Session expired", description: "Please try the payment again.", variant: "destructive" });
        return;
      }
      setTimeout(() => pollStatus(sessionId, reportId, attempt + 1), 2000);
    } catch (e) {
      setTimeout(() => pollStatus(sessionId, reportId, attempt + 1), 2000);
    }
  }, [fetchReport, setSearchParams, toast]);

  // Handle return from Stripe (once) or deep-link to an existing report
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    const reportId = searchParams.get("report_id");
    if (sessionId && reportId) {
      if (!paymentHandledRef.current) {
        paymentHandledRef.current = true;
        setVerifying(true);
        pollStatus(sessionId, reportId);
      }
    } else if (reportId && !report) {
      fetchReport(reportId);
    }
  }, [searchParams, report, fetchReport, pollStatus]);

  const handleUnlock = async (planId) => {
    if (!report) return;
    setPaying(true);
    try {
      const res = await axios.post(`${API}/seo/checkout`, {
        report_id: report.id,
        origin_url: window.location.origin,
        plan: planId,
      });
      window.location.href = res.data.url;
    } catch (err) {
      setPaying(false);
      toast({
        title: "Checkout failed",
        description: err?.response?.data?.detail || "Unable to start payment. Please try again.",
        variant: "destructive",
      });
    }
  };

  return { paying, verifying, handleUnlock };
};

// "My Audits" list for logged-in users
const useAuditHistory = (user, report, fetchReport, setNeedLogin) => {
  const [historyItems, setHistoryItems] = useState([]);

  const loadHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/seo/history`);
      setHistoryItems(res.data || []);
    } catch (e) {
      setHistoryItems([]);
    }
  }, []);

  useEffect(() => {
    if (user) loadHistory();
  }, [user, report, loadHistory]);

  const openHistoryReport = (id) => {
    setNeedLogin(false);
    fetchReport(id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return { historyItems, openHistoryReport };
};

// Composed API used by the SEO Auto Engine page
export const useSeoEngine = () => {
  const { toast } = useToast();
  const { user, login, logout } = useAuth();
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPlans, setShowPlans] = useState(false);

  const reportState = useReportState();
  const { report, setReport, needLogin, setNeedLogin, pendingReportId, setPendingReportId, fetchReport } = reportState;
  const payment = usePaymentFlow(report, fetchReport, toast);
  const history = useAuditHistory(user, report, fetchReport, setNeedLogin);

  // After any login method completes, fetch the report the user was gated on
  useEffect(() => {
    if (user && pendingReportId && !report) fetchReport(pendingReportId);
  }, [user, pendingReportId, report, fetchReport]);

  const startLogin = (reportId) => {
    if (reportId) sessionStorage.setItem("pending_report_id", reportId);
    login();
  };

  const handleAnalyze = async (e) => {
    e.preventDefault();
    if (!url.trim()) {
      toast({ title: "Enter a URL", description: "Please enter a website URL to analyze.", variant: "destructive" });
      return;
    }
    setLoading(true);
    setReport(null);
    setNeedLogin(false);
    try {
      const res = await axios.post(`${API}/seo/analyze`, { url });
      if (user) {
        await fetchReport(res.data.id);
      } else {
        setPendingReportId(res.data.id);
        setNeedLogin(true);
      }
    } catch (err) {
      toast({
        title: "Analysis failed",
        description: err?.response?.data?.detail || "Could not analyze that URL. Try another.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!report?.is_paid) return;
    window.open(`${API}/seo/report/${report.id}/download`, "_blank");
  };

  return {
    user, url, setUrl, loading, report, needLogin, pendingReportId,
    showPlans, setShowPlans,
    paying: payment.paying, verifying: payment.verifying, handleUnlock: payment.handleUnlock,
    historyItems: history.historyItems, openHistoryReport: history.openHistoryReport,
    startLogin, logout, handleAnalyze, handleDownload,
  };
};
